import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiClientError } from '../../lib/api';
import { PageHeader } from '../../pages/AppShell';
import { Banner, PageLoading } from '../../components/ui';
import Icon from '../../components/Icon';
import MapView, { hasTiles } from './MapView';
import type { LivePerson, LiveResponse, LiveState, TeamMemberSetup } from './types';

const STATE_LABEL: Record<LiveState, string> = {
  moving: 'Moving', stopped: 'Stopped', stale: 'No signal', silent: 'Never reported',
};
// The phone-reported reason a rep has gone quiet. Red = the rep did it
// (GPS/permission off); amber = the phone or network did (data lost).
const ALERT_META: Record<string, { label: string; danger: boolean }> = {
  gps_off: { label: 'GPS stopped', danger: true },
  permission_revoked: { label: 'Location permission off', danger: true },
  internet_lost: { label: 'Internet lost', danger: false },
  app_killed: { label: 'Tracking killed by phone', danger: false },
};
const clockOf = (iso: string) => new Date(iso)
  .toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
const initials = (n: string) => n.split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
const km = (n: number) => `${n.toFixed(1)} km`;

function ago(seconds: number): string {
  if (seconds < 60) return 'just now';
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m} min ago`;
  return `${Math.round(m / 60)} h ago`;
}

export default function TrackingLivePage() {
  const navigate = useNavigate();
  const [data, setData] = useState<LiveResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get<LiveResponse>('/tracking/live');
      setData(res);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not load the live map.');
    }
  }, []);

  useEffect(() => {
    void load();
    // The live map is worth keeping fresh; 15s is frequent enough to feel
    // live without hammering the server or the reps' batteries.
    timer.current = window.setInterval(() => void load(), 15000);
    return () => { if (timer.current) window.clearInterval(timer.current); };
  }, [load]);

  const people = data?.people ?? [];
  const counts = useMemo(() => ({
    total: people.length,
    attention: people.filter((p) => p.state === 'stale' || p.state === 'silent' || p.unloggedStops > 0).length,
  }), [people]);

  if (error && !data) {
    return (<><PageHeader title="Team map" /><div className="page"><Banner kind="danger">{error}</Banner></div></>);
  }
  if (!data) {
    return (<><PageHeader title="Team map" /><div className="page"><PageLoading rows={3} /></div></>);
  }
  if (!data.enabled) {
    return (
      <>
        <PageHeader title="Team map" subtitle="Where your field team is, right now." />
        <div className="page">
          <Banner kind="info">
            Live tracking is turned off. Turn it on in Settings, and your reps&apos; phones start
            reporting their route between check-in and check-out.
          </Banner>
        </div>
      </>
    );
  }

  const asOf = new Date(data.asOf);

  return (
    <>
      <PageHeader
        title="Team map"
        subtitle={`${counts.total} on shift${counts.attention ? ` · ${counts.attention} need a look` : ''}`}
        actions={(
          <>
            <span className="tk-live-dot" title="Auto-refreshing">
              <span className="dot" /> Live · {asOf.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </span>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate('/tracking/replay')}>
              Route replay
            </button>
          </>
        )}
      />
      <div className="tk-layout">
        <div className="tk-mapcol">
          {people.length === 0 ? (
            <div className="tk-empty-map">Nobody is checked in right now.</div>
          ) : (
            <MapView mode="live" people={people} selectedId={selected} onSelect={setSelected} />
          )}
          {!hasTiles && people.length > 0 && (
            <div className="tk-nomap">Route shape only — add a map key for streets (see docs/tracking.md).</div>
          )}
        </div>

        <aside className="tk-panel">
          <div className="tk-panel-scroll">
            {people.length === 0 ? (
              <p className="faint small" style={{ padding: 12 }}>No reps on shift.</p>
            ) : people.map((p) => (
              <RepRow
                key={p.user.id}
                p={p}
                active={selected === p.user.id}
                onSelect={() => setSelected(p.user.id === selected ? null : p.user.id)}
                onReplay={() => navigate('/tracking/replay', { state: { userId: p.user.id } })}
              />
            ))}
            <TeamSetup team={data.team} />
          </div>
        </aside>
      </div>
    </>
  );
}

function RepRow({ p, active, onSelect, onReplay }: {
  p: LivePerson; active: boolean; onSelect: () => void; onReplay: () => void;
}) {
  const color = `var(--tk-${p.state})`;
  const detail = p.atCustomer ? `at ${p.atCustomer.name}` : STATE_LABEL[p.state];

  const batt = p.session.last?.batteryPct ?? p.session.batteryPct ?? null;
  const battColor = batt == null ? 'var(--ink-faint)'
    : batt <= 15 ? 'var(--danger)'
      : batt <= 30 ? 'var(--warning)'
        : 'var(--ink-muted)';

  // A rep whose background permissions are missing keeps sending points
  // while the app is open, then goes silent the instant their screen locks
  // — a gap the manager would otherwise have no way to explain. Surface it.
  const alertMeta = p.alert ? ALERT_META[p.alert.kind] : undefined;

  const perms = p.permissions;
  // The short name of the missing setting goes on the chip itself, so a
  // manager sees WHICH setting without hovering; the full "why it matters"
  // sentence stays in the tooltip.
  const setup = !perms ? null
    : perms.bgLocationGranted === false
      ? { chip: 'Location not “Always”', why: 'Location not set to "Always" — tracking stops when the screen locks' }
      : perms.batteryExempt === false
        ? { chip: 'Battery optimisation on', why: 'Battery optimisation is on — the phone may kill tracking' }
        : null;

  const appVersion = p.session.device?.app ?? null;

  return (
    <div className={`tk-rep${active ? ' active' : ''}`} onClick={onSelect} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onSelect(); }}>
      <div className="tk-avatar" style={{ background: color }}>
        {p.user.avatarUrl ? <img src={p.user.avatarUrl} alt="" /> : initials(p.user.fullName)}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="tk-rep-name">{p.user.fullName}</div>
        <div className="tk-rep-sub">
          <span className="dot" style={{ background: color }} />
          {detail} · {p.state === 'silent' ? 'no signal today' : ago(p.silentForSeconds)}
          {batt != null ? (
            <>{' · '}<span style={{ color: battColor, fontWeight: 600 }}>{batt}%</span></>
          ) : null}
          {appVersion ? <span className="faint">{' · '}v{appVersion}</span> : null}
        </div>
        {p.alert && alertMeta ? (
          <div
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 4,
              fontSize: 11.5, fontWeight: 600,
              color: alertMeta.danger ? 'var(--danger)' : 'var(--warning)',
              background: alertMeta.danger ? 'var(--danger-soft)' : 'var(--warning-soft)',
              padding: '2px 8px', borderRadius: 999,
            }}
          >
            <Icon name="alert" size={12} /> {alertMeta.label} · since {clockOf(p.alert.since)}
          </div>
        ) : null}
        {setup ? (
          <div
            title={setup.why}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 4,
              fontSize: 11.5, fontWeight: 600, color: 'var(--warning)',
              background: 'var(--warning-soft)', padding: '2px 8px', borderRadius: 999,
            }}
          >
            <Icon name="alert" size={12} /> {setup.chip}
          </div>
        ) : null}
      </div>
      <div className="tk-rep-right">
        {p.unloggedStops > 0
          ? <span className="tk-flag">{p.unloggedStops} unlogged</span>
          : <span className="tk-km">{km(p.session.distanceKm)}</span>}
        <button type="button" className="tk-viewroute" onClick={(e) => { e.stopPropagation(); onReplay(); }}>
          Route ›
        </button>
      </div>
    </div>
  );
}

/**
 * The whole team's device-setup status, not just the reps on shift. A rep
 * whose phone is missing a background-tracking permission is listed with
 * what to fix; reps that have never reported are counted quietly below, so
 * a manager can chase a setup before the day they rely on it.
 */
function TeamSetup({ team }: { team: TeamMemberSetup[] }) {
  const needs = team.filter((t) => t.needsSetup);
  const unknown = team.filter(
    (t) => !t.needsSetup
      && t.permissions.bgLocationGranted == null
      && t.permissions.batteryExempt == null,
  );
  if (needs.length === 0 && unknown.length === 0) return null;

  return (
    <div style={{ borderTop: '1px solid var(--border)', marginTop: 8, paddingTop: 8 }}>
      <div style={{
        padding: '0 12px 6px', fontSize: 11.5, fontWeight: 700,
        letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--ink-faint)',
      }}>
        Device setup
      </div>
      {needs.map((t) => {
        const issue = t.permissions.bgLocationGranted === false
          ? 'Location not set to "Always"'
          : t.permissions.batteryExempt === false
            ? 'Battery optimisation is on'
            : 'Needs setup';
        return (
          <div key={t.user.id}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px' }}>
            <span style={{ color: 'var(--warning)', display: 'inline-flex', flex: 'none' }}>
              <Icon name="alert" size={15} />
            </span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{
                fontSize: 13.5, fontWeight: 600, color: 'var(--ink)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {t.user.fullName}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--warning)' }}>
                {issue}{t.onShift ? ' · on shift now' : ''}
              </div>
            </div>
          </div>
        );
      })}
      {unknown.length > 0 ? (
        <div style={{ padding: '6px 12px 10px', fontSize: 11.5, color: 'var(--ink-faint)' }}>
          {unknown.length} rep{unknown.length > 1 ? 's have' : ' has'} not reported device setup yet.
        </div>
      ) : null}
    </div>
  );
}
